import { FieldErrors } from "react-hook-form";

export interface ScrollFirstFormErrorOptions {
    behavior?: ScrollBehavior; // 'auto' | 'smooth' | 'instant'
    root?: HTMLElement; // scrollable container root. If missing -> no-op (returns false).
    topOffset?: number; // vertical padding to keep above the target after scroll (default 16)
    severities?: string[]; // whitelist of severities (data-error-severity). Omit to allow all.
}

/**
 * Flatten RHF FieldErrors into a list of dot‑paths.
 * We only need paths that actually carry a message.
 */
function collectErrorFieldNames(errors: FieldErrors, prefix = ""): string[] {
    const names: string[] = [];
    Object.entries(errors).forEach(([key, value]) => {
        const path = prefix ? `${prefix}.${key}` : key;
        if (value == null) return;

        const isErrorObject = typeof value === "object" && !Array.isArray(value) && (value as any).message;
        const isPrimitiveMessage = !isErrorObject && (typeof value === "string" || typeof value === "number");

        if (isErrorObject || isPrimitiveMessage) {
            names.push(path);
            return;
        }

        // Recurse into nested objects that look like grouped field errors (but avoid Date / other instances)
        if (
            typeof value === "object" &&
            !Array.isArray(value) &&
            Object.prototype.toString.call(value) === "[object Object]"
        ) {
            names.push(...collectErrorFieldNames(value as FieldErrors, path));
        }
    });
    return names;
}

/**
 * Scroll (at most once per submit) to the top-most errored field if it is outside the current viewport.
 * Updated heuristic (stabilized to avoid "walking" through errors over repeated submits):
 * 1. Collect all visible error DOM nodes (data-error-name / id^="error-").
 * 2. Filter by severity whitelist (if provided).
 * 3. Sort by visual top (ascending) -> ordered list.
 * 4. Take the FIRST (top-most) error element as the canonical target.
 * 5. If that element is already at least partially within the container viewport -> no scroll (leave stable).
 * 6. Otherwise scroll minimally so its top sits (topOffset) from container top (clamped inside range).
 * 7. No sequential advancement to the next errors; user must resolve the top-most first to change target.
 * Returns true if a scroll was initiated, false otherwise.
 *
 * Assumptions:
 * - Each FormErrorMessage sets data-error-name / id consistently.
 * - The provided root (or its nearest scrollable ancestor) governs scrolling.
 */
export function scrollFirstFormError(
    errors: FieldErrors | undefined,
    { behavior = "instant", root, topOffset = 16, severities }: ScrollFirstFormErrorOptions,
): boolean {
    // root is required for scrolling; silently no-op if absent (hook may call before ref is set)
    if (!root) return false;
    if (!errors) return false;
    const names = collectErrorFieldNames(errors);
    if (!names.length) return false;
    const doc: Document = (root?.ownerDocument as Document) || document;
    const effectiveRoot: HTMLElement | null = root ?? null;

    // Collect potential error nodes within scope (root or document if somehow root missing – but guarded above).
    let targetEl: HTMLElement | null = null;
    const scope: ParentNode = effectiveRoot ?? doc;
    let allErrorEls = Array.from(scope.querySelectorAll<HTMLElement>('[data-error-name], [id^="error-"]'));
    if (severities && severities.length > 0) {
        const allow = new Set(severities.map((s) => s.toLowerCase()));
        allErrorEls = allErrorEls.filter((el) => {
            const sev = (el.getAttribute("data-error-severity") || "").toLowerCase();
            return allow.has(sev);
        });
    }
    if (!allErrorEls.length) {
        return false;
    }
    // Fallback initialization (will be replaced if a below/above candidate is found)
    targetEl = allErrorEls[0];

    try {
        if (effectiveRoot) {
            // If provided root is not scrollable, climb ancestors to find a scrollable container.
            let container: HTMLElement | null = effectiveRoot;
            const isScrollable = (el: HTMLElement) => el.scrollHeight > el.clientHeight + 4; // tolerance
            if (!isScrollable(container)) {
                let parent: HTMLElement | null = container.parentElement;
                while (parent) {
                    if (isScrollable(parent)) {
                        container = parent;
                        break;
                    }
                    parent = parent.parentElement;
                }
            }
            if (!container) {
                return false;
            }
            const containerRect = container.getBoundingClientRect();
            // Sort error elements by their top coordinate (stable, we only ever consider the first/top error)
            const ordered = allErrorEls
                .slice()
                .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);
            targetEl = ordered[0];
            const tolerance = 4; // minor tolerance for boundary jitter
            const elRectInitial = targetEl.getBoundingClientRect();
            const isWithin =
                elRectInitial.bottom >= containerRect.top + tolerance &&
                elRectInitial.top <= containerRect.bottom - tolerance;
            if (isWithin) {
                return false; // Top-most error already visible -> no scroll (prevents sequential jumping)
            }
            // Proceed to compute minimal scroll for this top-most error.
            const elRect = targetEl.getBoundingClientRect();
            // Minimal scroll: align target's top to topOffset (no center/end logic – deterministic & small delta)
            let targetScroll = container.scrollTop + (elRect.top - containerRect.top) - topOffset;
            // Clamp into scroll range
            const maxScroll = container.scrollHeight - container.clientHeight;
            if (targetScroll < 0) targetScroll = 0;
            if (targetScroll > maxScroll) targetScroll = maxScroll;
            // If already effectively at desired offset, skip (avoid micro-scroll jitter)
            const currentTopInside = elRect.top - containerRect.top;
            if (Math.abs(currentTopInside - topOffset) < 1) {
                return false; // already effectively at desired offset
            }
            // Scroll (native smooth if requested)

            if (behavior === "smooth") {
                try {
                    // Try native smooth scroll if supported
                    container.scrollTo({ top: targetScroll, behavior: "smooth" });
                } catch {
                    container.scrollTop = targetScroll; // fallback
                }
            } else {
                container.scrollTop = targetScroll;
            }
            return true;
        }
        return false;
    } catch {
        return false;
    }
}
